import { useEffect, useState, useRef } from "react";
import { IconSend, IconArrowDownLine } from "@resources/icons/Icons";
import _, { throttle } from "lodash";
import { useCurrentTimezone } from "@hooks/useCurrentTimezone";
import { useInsightTypeConverter } from "@hooks/useInsightTypeConverter";
import { AnalyticsPromptDto } from "@open-api";
import { HVButton, HVIcon } from "@repo/HVComponents";
import { useGetAiInsight } from "@queries/tanstack/ai-controller/AiController";
import { useLang } from "@hooks/useLang";
import * as S from "./GenerativeAI.styled";
import MessageItem from "../message-item";
import Card from "../card";
import { ICON } from "../../constants/icons";
import { predefinedPrompt, PromptItem } from "./RecommendPrompt";

interface GeneratvieAIProps {
  title: string;
  isShow: boolean;
  searchFilter?: {
    baseTime: { start: string; end: string } | undefined;
  };
  ruleGroup?: {
    [p: string]: _.Dictionary<
      {
        locationId: string;
        locationName: string;
        deviceId: string;
        ruleName: string;
        channel: number;
        ruleType: string;
      }[]
    >;
  };
  onChangeInsightChat: (title: string) => void;
  selectedLocationId: string;
}

const GenerativeAI = (props: GeneratvieAIProps) => {
  const {
    title,
    isShow,
    searchFilter,
    ruleGroup,
    onChangeInsightChat,
    selectedLocationId
  } = props;
  const { translate } = useLang();
  const [message, setMessage] = useState("");
  const [list, setList] = useState<
    Array<{ role: string; message: string; state: string }>
  >([]);
  const [isComposing, setIsComposing] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const { convertByTimezone } = useCurrentTimezone();
  const { labelToInsightType, getGroupByTypeAndLocationId } =
    useInsightTypeConverter();
  const { mutateAsync: mutateChat, isLoading } = useGetAiInsight();
  const chatRef = useRef<HTMLDivElement>(null);
  const scrollHandlerRef = useRef<(() => void) | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const prevTitleRef = useRef<string | null>(null);

  useEffect(() => {
    scrollHandlerRef.current = throttle(() => {
      if (chatRef.current) {
        const { scrollTop, clientHeight, scrollHeight } = chatRef.current;

        const atBottom = scrollTop + clientHeight < scrollHeight - 10;
        setShowScrollButton(atBottom);
      }
    }, 300);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    const chatEl = chatRef.current;
    const handleScroll = () => scrollHandlerRef.current?.();

    chatEl?.addEventListener("scroll", handleScroll);

    return () => {
      chatEl?.removeEventListener("scroll", handleScroll);
    };
  }, [list]);

  useEffect(() => {
    if (!isShow) {
      setMessage("");
      setList([]);
      setShowScrollButton(false);
      return;
    }

    if (isLoading) {
      setList((prev) => [
        ...prev,
        { role: "assistant", message: "", state: "loading" }
      ]);
    }
  }, [isShow, isLoading]);

  useEffect(() => {
    if (
      !isLoading &&
      prevTitleRef.current !== null &&
      prevTitleRef.current !== title
    ) {
      setMessage("");
      setList([]);
    }

    if (!isLoading) {
      prevTitleRef.current = title;
    }
  }, [title, isLoading]);

  const scrollToBottom = () => {
    if (chatRef.current) {
      chatRef.current.scrollTo({
        top: chatRef.current.scrollHeight,
        behavior: "smooth"
      });
    }
  };

  const autoResize = (e: React.FormEvent<HTMLTextAreaElement>) => {
    const target = e.target as HTMLTextAreaElement;
    target.style.height = "auto";
    target.style.height =
      target.scrollHeight >= 192 ? "192px" : `${target.scrollHeight}px`;
    setMessage(target.value);
  };
  const handleSubmit = (msg: string, boolean: boolean) => {
    // 필터링 boolean타입이 true면 스킵, false면 필터링 추가
    if (isLoading || !msg.trim() || !ruleGroup) return;
    setList((prev) => [
      ...prev,
      { role: "user", message: msg.trim(), state: "done" }
    ]);

    const insightType = labelToInsightType(title);
    const groupByLocations = getGroupByTypeAndLocationId(
      ruleGroup,
      title,
      selectedLocationId
    );

    const startTime = searchFilter?.baseTime?.start;
    const endTime = searchFilter?.baseTime?.end;

    if (!startTime || !endTime) {
      console.error("Base time range is missing");
      return;
    }
    setMessage("");
    mutateChat({
      start: convertByTimezone(startTime),
      end: convertByTimezone(endTime),
      insightType,
      body: {
        rules: groupByLocations,
        prompt: {
          text: msg.trim(),
          skipValidation: boolean
        }
      } as AnalyticsPromptDto
    }).then((r) => {
      if (r) {
        setList((prev) =>
          prev.map((item) =>
            item.state === "loading" && item.role === "assistant"
              ? {
                  role: r.message.role,
                  message: r.message.content[0].text,
                  state: "done"
                }
              : item
          )
        );
      } else {
        setList((prev) =>
          prev.map((item) =>
            item.state === "loading" && item.role === "assistant"
              ? {
                  role: "assistant",
                  message:
                    "Sorry. Please ask a question related to chart analysis.",
                  state: "done"
                }
              : item
          )
        );
      }
      if (inputRef.current) {
        inputRef.current.focus();
      }
    });
    setTimeout(() => {
      scrollToBottom();
    }, 100);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      if (isComposing) return;
      event.preventDefault(); // 줄바꿈 방지
      handleSubmit(message, false);
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (!e.target.value) return;
    setMessage(e.target.value);
  };

  const onSendChat = (msg: string) => {
    handleSubmit(msg, true); // flag true is skip
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const onReset = () => {
    if (!list.some((item) => item.state === "loading")) {
      setMessage("");
      setList([]);
      setShowScrollButton(false);
    }
  };
  const onClose = () => {
    if (!list.some((item) => item.state === "loading")) {
      onChangeInsightChat("");
    }
  };

  const renderCardsByType = (
    type: string | null,
    onSendChat: (msg: string) => void
  ): JSX.Element | null => {
    if (!type) return null;
    const dataList: PromptItem[] = predefinedPrompt[type] || [];
    return (
      <>
        {dataList.map((item) => (
          <Card title={item.title} desc={item.desc} onSendChat={onSendChat} />
        ))}
      </>
    );
  };

  return (
    <S.Container>
      <S.GenHeader>
        <S.GenTitle>{prevTitleRef.current}</S.GenTitle>
        <HVButton
          variant="Ghost"
          buttonContainerProps={{
            w: 32,
            h: 32,
            p: 0
          }}
          data-testid="generativeAI-btn-reset"
          aria-label="Reset"
          onClick={onReset}
        >
          <HVIcon name="reset" svgProps={{ ...ICON.SIZE_20 }} />
        </HVButton>
        <HVButton
          variant="Ghost"
          buttonContainerProps={{
            w: 32,
            h: 32,
            p: 0
          }}
          data-testid="generativeAI-btn-close"
          aria-label="Close"
          onClick={onClose}
        >
          <HVIcon name="close" svgProps={{ ...ICON.SIZE_20 }} />
        </HVButton>
      </S.GenHeader>
      <S.GenContent id="chat-container" ref={chatRef}>
        {list && list.length > 0 ? (
          list.map((item) => {
            return (
              <MessageItem
                own={item.role === "user"}
                message={item.message}
                state={item.state}
              />
            );
          })
        ) : (
          <>
            <S.ContentTitle>
              {translate("recommend_prompt")}
              <span>{prevTitleRef.current?.toLowerCase()}</span>
            </S.ContentTitle>
            <S.CardContainer>
              {renderCardsByType(prevTitleRef.current, onSendChat)}
            </S.CardContainer>
          </>
        )}
      </S.GenContent>
      <S.ChatFooter
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(message, false);
        }}
      >
        <S.ChatInput
          ref={inputRef}
          placeholder="Message..."
          className="message-input"
          maxLength={500}
          rows={1}
          value={message}
          onInput={autoResize}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onCompositionStart={() => setIsComposing(true)}
          onCompositionEnd={() => setIsComposing(false)}
        />
        <S.SendIcon active={message.trim().length > 0} disabled={isLoading}>
          <IconSend />
        </S.SendIcon>
      </S.ChatFooter>
      {showScrollButton && (
        <S.ScrollButton onClick={scrollToBottom}>
          <IconArrowDownLine width={24} height={24} />
        </S.ScrollButton>
      )}
    </S.Container>
  );
};

export default GenerativeAI;
